import { Input } from 'components/custom/input';
import { ANY_OBJECT, APP, DOMAIN_TYPE, PROTOCOL_TYPE, translateLabel } from 'constants/format';
import { useTranslate } from 'context/TranslateContext';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap';
import { GlobalTypes } from 'redux/types';
import { getAPI } from 'services/API/Aml_Domain';
import { AmlDomainProps } from 'services/API/Aml_Domain/interface';
import { useSwal } from 'helpers/sweetalert';
import { validate } from './validator';

export interface BankModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  getData: () => Promise<any>;
  amlDomain?: AmlDomainProps;
}

const initialState: AmlDomainProps = {
  id: '',
  name: '',
  domain: '',
  protocol: 'https',
  type: '1',
};

export const CreateAmlDomainModal = ({ isOpen, setIsOpen, getData, amlDomain }: BankModalProps) => {
  const {
    auth: {
      user: { company_id },
    },
  } = useSelector((state: GlobalTypes.RootState) => state);
  const [Swal] = useSwal();
  const { translate } = useTranslate();

  const [form, setForm] = useState<AmlDomainProps>(initialState);
  const [errors, setErrors] = useState<ANY_OBJECT>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { name, domain, protocol, type } = form;

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    let value = e.target.value;
    value = e.target.name === 'domain' ? value.toLowerCase() : value;
    if (e.target.name === 'domain' && value.includes('://')) {
      const domainArr = value.split('://');
      value = domainArr[1];

      let isApp = false;
      isApp = isApp || value.includes('play.google.com');
      isApp = isApp || value.includes('apps.apple.com');

      setForm({
        ...form,
        [e.target.name]: value,
        protocol: domainArr[0] as 'http' | 'https',
        type: isApp ? APP + '' : form.type,
      });
      return;
    }

    setForm({
      ...form,
      [e.target.name]: value,
    });
  };

  const toggle = () => {
    setIsOpen(!isOpen);
    setForm(initialState);
    setErrors({});
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    const { isValid, errors } = validate(form);
    setErrors(errors);
    if (!isValid) {
      setIsSubmitting(false);
      return;
    }

    const api = getAPI(company_id, form.id);
    let res;
    if (amlDomain) {
      res = await api.update(form);
    } else {
      res = await api.create(form);
    }

    if (!res.success) {
      Swal.fire({
        title: translate('aml_domain'),
        text: res?.message || 'Oops',
        icon: 'error',
      });
      setIsSubmitting(false);

      return;
    }
    await getData();
    setIsSubmitting(false);
    toggle();
  };

  useEffect(() => {
    if (amlDomain) {
      setForm(amlDomain);
    }
    // eslint-disable-next-line
  }, [amlDomain]);

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {translate(amlDomain ? 'edit' : 'create')}
        {translate('aml_domain')}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={onSubmit}>
          <Input
            legend={translate('name').toUpperCase()}
            name="name"
            value={name}
            onChange={onChange}
            type="text"
            error={translate(...(errors.name ?? ''))}
          />

          <Input
            type="select-text"
            options={PROTOCOL_TYPE}
            selectName="protocol"
            selectValue={protocol}
            handleSelectChange={onChange}
            name="domain"
            value={domain}
            onChange={onChange}
            legend={translate('domain_name').toUpperCase()}
            placeholder="e.g.: example.com"
            isRequired
            error={translate(...(errors.domain ?? ''))}
          />

          <Input
            type="select"
            options={translateLabel(DOMAIN_TYPE, translate, true)}
            value={type}
            onChange={onChange}
            legend={translate('type').toUpperCase()}
            name="type"
            isRequired
            error={translate(...(errors.type ?? ''))}
          />

          <div className="d-grid">
            <button className="btn btn-primary mt-3">
              {isSubmitting ? <Spinner size="sm" /> : translate(amlDomain ? 'edit' : 'create')}
            </button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};
